import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import PlaceAutocomplete from '../common/PlaceAutocomplete';

import {
  fetchRecurringRides,
  updateRecurringOrder
} from '../../redux/slices/ridesSlice';
import { db } from '../../firebase/config';
import { VEHICLE_TYPE_OPTIONS } from '../../config/vehicleTypes';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const WEEKDAYS = [
  { value: 0, label: 'Sonntag' },
  { value: 1, label: 'Montag' },
  { value: 2, label: 'Dienstag' },
  { value: 3, label: 'Mittwoch' },
  { value: 4, label: 'Donnerstag' },
  { value: 5, label: 'Freitag' },
  { value: 6, label: 'Samstag' }
];

const RecurringOrderEditForm = () => {
  const { recurringOrderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { recurringOrders, isLoading, error } = useSelector(state => state.rides);

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    start: '',
    destination: '',
    start_location: { latitude: null, longitude: null },
    destination_location: { latitude: null, longitude: null },
    vehicle_type: 1,
    recurring_weekdays: [],
    recurring_time: new Date().setHours(8, 0, 0, 0),
    current_end_date: null,
    new_end_date: null,
    is_rechnungsfahrt: false,
    invoice_customer_id: ''
  });

  const [currentOrder, setCurrentOrder] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preview, setPreview] = useState({ updateCount: 0, deleteCount: 0, newCount: 0 });
  const [invoiceCustomers, setInvoiceCustomers] = useState([]);
  // Krankenfahrt-Info der Serie (aus der Referenzfahrt): Das Formular kann
  // Krankenfahrt/Verordnung nicht bearbeiten (nur RideForm), aber es muss
  // die Exklusivität wahren — Kranken- und Rechnungsfahrt schließen sich aus.
  const [medicalInfo, setMedicalInfo] = useState(null);

  useEffect(() => {
    // Lade Daueraufträge wenn noch nicht geladen
    if (recurringOrders.length === 0) {
      dispatch(fetchRecurringRides());
    }
  }, [dispatch, recurringOrders.length]);

  // Aktive Rechnungskunden laden (für die Rechnungsfahrt-Zuordnung).
  useEffect(() => {
    let active = true;
    getDocs(query(collection(db, 'invoiceCustomers'), orderBy('displayName')))
      .then((snap) => {
        if (!active) return;
        setInvoiceCustomers(
          snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.active !== false)
        );
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const order = recurringOrders.find(o => o.id === recurringOrderId);
    if (!order) return;

    setCurrentOrder(order);

    const toDate = (t) => (t?.toDate ? t.toDate() : new Date(t));
    const now = Date.now();

    // Nur noch nicht gestartete/abgeschlossene Fahrten sind für die Bearbeitung relevant.
    const futureRides = (order.rides || [])
      .filter(r => ['scheduled', 'pre_accepted'].includes(r.status))
      .filter(r => toDate(r.scheduledTime).getTime() > now)
      .sort((a, b) => toDate(a.scheduledTime) - toDate(b.scheduledTime));

    // Referenzfahrt: erste zukünftige Fahrt. Fallback auf die allgemein erste Fahrt,
    // damit das Formular auch bei bereits abgelaufenen Serien noch befüllt wird.
    const referenceRide = futureRides[0] || order.rides?.[0];
    if (!referenceRide) return;

    const weekdays = [...new Set(futureRides.map(r => toDate(r.scheduledTime).getDay()))];
    const weekdaysFallback = weekdays.length > 0
      ? weekdays
      : [...new Set((order.rides || []).map(r => toDate(r.scheduledTime).getDay()))];

    const lastFutureRide = futureRides[futureRides.length - 1];
    const lastRideDate = lastFutureRide ? toDate(lastFutureRide.scheduledTime) : new Date();

    setFormData({
      customer_name: referenceRide.customerName || '',
      customer_phone: referenceRide.customerPhone || '',
      start: referenceRide.start || '',
      destination: referenceRide.destination || '',
      start_location: {
        latitude: referenceRide.startLatitude || null,
        longitude: referenceRide.startLongitude || null,
      },
      destination_location: {
        latitude: referenceRide.destinationLatitude || null,
        longitude: referenceRide.destinationLongitude || null,
      },
      vehicle_type: referenceRide.vehicleType || 1,
      recurring_weekdays: weekdaysFallback,
      recurring_time: toDate(referenceRide.scheduledTime).getTime(),
      current_end_date: lastRideDate,
      new_end_date: lastRideDate,
      // Bei Krankenfahrt-Serien immer false — repariert nebenbei Altdaten,
      // die fälschlich beide Flags tragen (Exklusivität, s. RideForm).
      is_rechnungsfahrt: referenceRide.isKrankenfahrt ? false : (referenceRide.isRechnungsfahrt || false),
      invoice_customer_id: referenceRide.isKrankenfahrt ? '' : (referenceRide.invoiceCustomerId || ''),
    });

    setMedicalInfo(referenceRide.isKrankenfahrt
      ? {
          patientName: referenceRide.medicalTransportSnapshot?.patientName || '',
          hasOrder: !!referenceRide.medicalTransportOrderId,
        }
      : null);
  }, [recurringOrders, recurringOrderId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWeekdayChange = (weekday) => {
    setFormData(prev => ({
      ...prev,
      recurring_weekdays: prev.recurring_weekdays.includes(weekday)
        ? prev.recurring_weekdays.filter(w => w !== weekday)
        : [...prev.recurring_weekdays, weekday]
    }));
  };

  const handleTimeChange = (e) => {
    const timeString = e.target.value; // "HH:MM"
    const [hours, minutes] = timeString.split(':').map(Number);
    const timeDate = new Date();
    timeDate.setHours(hours, minutes, 0, 0);
    
    setFormData(prev => ({
      ...prev,
      recurring_time: timeDate.getTime()
    }));
  };

  const buildUpdateData = () => {
    const firstRide = currentOrder?.rides?.[0];

    const startCoords = formData.start_location?.latitude
      ? {
          startLatitude: formData.start_location.latitude,
          startLongitude: formData.start_location.longitude,
        }
      : firstRide
        ? { startLatitude: firstRide.startLatitude, startLongitude: firstRide.startLongitude }
        : {};

    const destCoords = formData.destination_location?.latitude
      ? {
          destinationLatitude: formData.destination_location.latitude,
          destinationLongitude: formData.destination_location.longitude,
        }
      : firstRide
        ? { destinationLatitude: firstRide.destinationLatitude, destinationLongitude: firstRide.destinationLongitude }
        : {};

    return {
      customerId: firstRide?.customerId,
      customerName: formData.customer_name,
      customerPhone: formData.customer_phone,
      start: formData.start,
      destination: formData.destination,
      vehicleType: formData.vehicle_type,
      ...startCoords,
      ...destCoords,
      // Zeitplan-Felder werden ab jetzt immer mitgesendet, damit Änderungen
      // an Uhrzeit oder Wochentagen auf alle zukünftigen Fahrten wirken.
      recurring_weekdays: formData.recurring_weekdays,
      recurring_time: new Date(formData.recurring_time),
      newEndDate: formData.new_end_date ? new Date(formData.new_end_date) : null,
      isRechnungsfahrt: formData.is_rechnungsfahrt,
      invoiceCustomerId: (formData.is_rechnungsfahrt && formData.invoice_customer_id) ? formData.invoice_customer_id : null,
      invoiceCustomerName: (formData.is_rechnungsfahrt && formData.invoice_customer_id)
        ? (invoiceCustomers.find((c) => c.id === formData.invoice_customer_id)?.displayName
           || invoiceCustomers.find((c) => c.id === formData.invoice_customer_id)?.companyName || null)
        : null,
      invoiceBillingStatus: (formData.is_rechnungsfahrt && formData.invoice_customer_id) ? 'pending' : null,
    };
  };

  const computePreview = () => {
    if (!currentOrder) return { updateCount: 0, deleteCount: 0, newCount: 0 };

    const toDate = (t) => (t?.toDate ? t.toDate() : new Date(t));
    const now = Date.now();
    const weekdays = formData.recurring_weekdays;
    const newTime = new Date(formData.recurring_time);

    const futureRides = (currentOrder.rides || [])
      .filter(r => ['scheduled', 'pre_accepted'].includes(r.status))
      .filter(r => toDate(r.scheduledTime).getTime() > now);

    let updateCount = 0;
    let deleteCount = 0;
    const occupiedDays = new Set();
    let lastKept = null;

    futureRides.forEach(r => {
      const scheduled = toDate(r.scheduledTime);
      const weekday = scheduled.getDay();

      if (!weekdays.includes(weekday)) {
        if (r.status === 'scheduled') {
          deleteCount += 1;
        } else {
          updateCount += 1;
        }
        return;
      }

      updateCount += 1;
      const key = `${scheduled.getFullYear()}-${scheduled.getMonth()}-${scheduled.getDate()}`;
      occupiedDays.add(key);

      const adjusted = new Date(scheduled);
      adjusted.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
      if (!lastKept || adjusted > lastKept) lastKept = adjusted;
    });

    let newCount = 0;
    const rangeEnd = formData.new_end_date ? new Date(formData.new_end_date) : (lastKept ? new Date(lastKept) : null);
    if (rangeEnd) {
      rangeEnd.setHours(23, 59, 59, 999);
      const rangeStart = new Date(Math.min(now, lastKept ? lastKept.getTime() : now));
      rangeStart.setHours(0, 0, 0, 0);
      const cursor = new Date(rangeStart);
      while (cursor <= rangeEnd) {
        const key = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
        if (weekdays.includes(cursor.getDay()) && !occupiedDays.has(key)) {
          const slot = new Date(cursor);
          slot.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
          if (slot.getTime() > now) newCount += 1;
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    return { updateCount, deleteCount, newCount };
  };

  const handleOpenConfirm = (e) => {
    e.preventDefault();
    if (!currentOrder) return;
    if (!formData.recurring_weekdays || formData.recurring_weekdays.length === 0) {
      setSubmitError('Mindestens ein Wochentag muss ausgewählt sein.');
      return;
    }
    if (formData.is_rechnungsfahrt && !formData.invoice_customer_id) {
      setSubmitError('Bitte einen Rechnungskunden auswählen.');
      return;
    }
    // Sicherheitsnetz analog RideForm validateForm(): getrennte Abrechnungs-
    // wege, eine Krankenfahrt-Serie darf nicht zusätzlich Rechnungsfahrt sein.
    if (medicalInfo && formData.is_rechnungsfahrt) {
      setSubmitError('Diese Serie ist eine Krankenfahrt — Kranken- und Rechnungsfahrt schließen sich aus.');
      return;
    }
    setSubmitError(null);
    setPreview(computePreview());
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await dispatch(updateRecurringOrder({
        recurringOrderId,
        updateData: buildUpdateData(),
      }));

      if (result.error) {
        throw new Error(result.error.message || 'Fehler beim Aktualisieren');
      }

      await dispatch(fetchRecurringRides());
      navigate('/fahrten/dauerauftraege');
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !currentOrder) {
    return <Loading message="Dauerauftrag wird geladen..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden des Dauerauftrags"
        message={error}
        onRetry={() => dispatch(fetchRecurringRides())}
      />
    );
  }

  if (!currentOrder) {
    return (
      <ErrorMessage
        title="Dauerauftrag nicht gefunden"
        message="Der angeforderte Dauerauftrag konnte nicht gefunden werden."
      />
    );
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    
    // Stelle sicher, dass es ein gültiges Date-Objekt ist
    const dateObj = date instanceof Date ? date : new Date(date);
    
    // Prüfe ob das Date-Objekt gültig ist
    if (isNaN(dateObj.getTime())) return '';
    
    return dateObj.toISOString().split('T')[0];
  };

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <PageHeader
        title="Dauerauftrag bearbeiten"
        subtitle={`Bearbeitung für ${currentOrder.customer_name}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          { label: 'Daueraufträge', path: '/fahrten/dauerauftraege' },
          { label: 'Bearbeiten' }
        ]}
      />

      <Paper sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/fahrten/dauerauftraege')}
            sx={{ mr: 2 }}
          >
            Zurück zur Übersicht
          </Button>
        </Box>

        <form onSubmit={handleOpenConfirm}>
          <Grid container spacing={3}>
            {/* Kundeninformationen */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                Kundeninformationen
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="customer_name"
                label="Kundenname"
                value={formData.customer_name}
                disabled
                sx={{ 
                  '& .MuiInputBase-root': { 
                    backgroundColor: 'grey.100' 
                  } 
                }}
                helperText="Kundendaten können nicht geändert werden"
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="customer_phone"
                label="Telefonnummer"
                value={formData.customer_phone}
                onChange={handleInputChange}
                helperText="Telefonnummer kann aktualisiert werden"
              />
            </Grid>

            {/* Route */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Route
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <PlaceAutocomplete
                fullWidth
                label="Startadresse"
                value={formData.start}
                onChange={(e) => setFormData(prev => ({ ...prev, start: e.target.value }))}
                onPlaceSelect={(address, location) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    start: address,
                    start_location: location
                  }));
                }}
                required
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <PlaceAutocomplete
                fullWidth
                label="Zieladresse"
                value={formData.destination}
                onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                onPlaceSelect={(address, location) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    destination: address,
                    destination_location: location
                  }));
                }}
                required
              />
            </Grid>

            {/* Fahrzeugtyp */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Fahrzeugtyp</InputLabel>
                <Select
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleInputChange}
                  label="Fahrzeugtyp"
                >
                  {VEHICLE_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Rechnungsfahrt */}
            <Grid size={12}>
              {medicalInfo && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Krankenfahrt-Serie{medicalInfo.patientName ? ` (Patient: ${medicalInfo.patientName})` : ''}
                  {medicalInfo.hasOrder ? ' mit verknüpfter Verordnung' : ' — noch ohne verknüpfte Verordnung'}.
                  Verordnungszuordnung bitte über die Bearbeitung der Einzelfahrt ändern; Kranken- und
                  Rechnungsfahrt schließen sich aus.
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={formData.is_rechnungsfahrt}
                      disabled={!!medicalInfo}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        is_rechnungsfahrt: e.target.checked,
                        invoice_customer_id: e.target.checked ? prev.invoice_customer_id : ''
                      }))}
                    />
                  }
                  label="Rechnungsfahrt"
                />
                {formData.is_rechnungsfahrt && (
                  <Autocomplete
                    options={invoiceCustomers}
                    getOptionLabel={(o) => `${o.customerNumber ? o.customerNumber + ' · ' : ''}${o.displayName || o.companyName || ''}`}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={invoiceCustomers.find((c) => c.id === formData.invoice_customer_id) || null}
                    onChange={(e, val) => setFormData(prev => ({ ...prev, invoice_customer_id: val ? val.id : '' }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Rechnungskunde"
                        required
                        error={!!submitError && !formData.invoice_customer_id}
                        helperText="Firma, die diese Fahrt bezahlt"
                      />
                    )}
                    sx={{ minWidth: { xs: 0, sm: 260 }, width: { xs: '100%', sm: 'auto' } }}
                  />
                )}
              </Box>
            </Grid>

            {/* Zeiten und Wiederholung */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Zeitplan
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="time"
                name="recurring_time"
                label="Uhrzeit"
                value={formatTime(formData.recurring_time)}
                onChange={handleTimeChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="body1" gutterBottom>
                Wochentage
              </Typography>
              <FormGroup row>
                {WEEKDAYS.map((day) => (
                  <FormControlLabel
                    key={day.value}
                    control={
                      <Checkbox
                        checked={formData.recurring_weekdays.includes(day.value)}
                        onChange={() => handleWeekdayChange(day.value)}
                      />
                    }
                    label={day.label}
                  />
                ))}
              </FormGroup>
            </Grid>

            {/* Datumsbereich */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                name="current_end_date"
                label="Aktuelles Enddatum"
                value={formatDateForInput(formData.current_end_date)}
                InputLabelProps={{ shrink: true }}
                disabled
                helperText="Das aktuelle Enddatum des Dauerauftrags"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                name="new_end_date"
                label="Neues Enddatum"
                value={formatDateForInput(formData.new_end_date)}
                onChange={(e) => {
                  const dateValue = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    new_end_date: dateValue ? new Date(dateValue) : null
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Verlängern Sie das Enddatum, um neue Fahrten zu erstellen"
              />
            </Grid>

            {/* Fehleranzeige */}
            {submitError && (
              <Grid size={12}>
                <Alert severity="error">
                  {submitError}
                </Alert>
              </Grid>
            )}

            {/* Submit Button */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                  size="large"
                >
                  {isSubmitting ? 'Wird gespeichert...' : 'Änderungen speichern'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => navigate('/fahrten/dauerauftraege')}
                  disabled={isSubmitting}
                  size="large"
                >
                  Abbrechen
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Änderungen am Dauerauftrag bestätigen</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Folgende Änderungen werden auf zukünftige Fahrten angewendet:
            <Box component="ul" sx={{ mt: 1, mb: 1 }}>
              <li>{preview.updateCount} Fahrt(en) werden aktualisiert (Adresse, Fahrzeug, Kontakt, Uhrzeit).</li>
              <li>{preview.deleteCount} Fahrt(en) werden entfernt (entfallene Wochentage).</li>
              <li>{preview.newCount} Fahrt(en) werden neu erstellt.</li>
            </Box>
            Bereits akzeptierte oder laufende Fahrten werden nicht angerührt.
            Diese Aktion kann nicht automatisch rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Wird gespeichert...' : 'Änderungen anwenden'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </APIProvider>
  );
};

export default RecurringOrderEditForm;